import { useQuery, useMutation } from '@tanstack/react-query';
import apiClient from './client';

// ── Types ──

export interface SentinelDashboard {
  cameras_active: number;
  cameras_total: number;
  active_tracking: number;
  zones_monitored: number;
  alerts_today: number;
  persons_enrolled: number;
  recent_events: SentinelEvent[];
}

export interface SentinelEvent {
  id: string;
  type: 'access_granted' | 'access_denied' | 'unknown_person' | 'liveness_fail' | 'zone_alert';
  description: string;
  timestamp: string;
  camera_id?: string;
  zone?: string;
  confidence?: number;
}

export interface SentinelStatus {
  status: 'operational' | 'degraded' | 'offline';
  uptime_seconds: number;
  modules_biometriques: {
    face_detection: boolean;
    face_recognition: boolean;
    liveness_detection: boolean;
    anti_spoofing: boolean;
  };
  gpu_available: boolean;
  model_version: string;
  last_check: string;
}

export interface EnrollRequest {
  person_name: string;
  person_id?: string;
  image_base64: string;
  clearance_level?: 'visitor' | 'employee' | 'admin' | 'restricted';
  metadata?: Record<string, string>;
}

export interface EnrollResponse {
  success: boolean;
  person_id: string;
  face_encoding_id: string;
  message: string;
}

export interface AccessCheckRequest {
  image_base64: string;
  zone_id?: string;
  required_clearance?: 'visitor' | 'employee' | 'admin' | 'restricted';
}

export interface AccessCheckResponse {
  access_granted: boolean;
  person_id: string | null;
  person_name: string | null;
  confidence: number;
  clearance_level: string | null;
  reason: string;
  liveness_passed: boolean;
}

export interface VerifyRequest {
  image_base64: string;
  person_id: string;
}

export interface VerifyResponse {
  verified: boolean;
  confidence: number;
  person_name: string;
  message: string;
}

export interface IdentifyRequest {
  image_base64: string;
  top_k?: number;
}

export interface IdentifyMatch {
  person_id: string;
  person_name: string;
  confidence: number;
  clearance_level: string;
}

export interface IdentifyResponse {
  identified: boolean;
  matches: IdentifyMatch[];
  message: string;
}

export interface LivenessRequest {
  image_base64: string;
}

export interface LivenessResponse {
  is_live: boolean;
  confidence: number;
  spoofing_type: string | null;
  message: string;
}

// ── Helpers ──

const CLEARANCE_MAP: Record<string, number> = {
  visitor: 1,
  employee: 2,
  admin: 3,
  restricted: 4,
};

function base64ToBlob(base64: string): Blob {
  const byteChars = atob(base64);
  const byteNums = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNums[i] = byteChars.charCodeAt(i);
  }
  return new Blob([new Uint8Array(byteNums)], { type: 'image/jpeg' });
}

function makeImageFormData(base64: string): FormData {
  const formData = new FormData();
  formData.append('image', base64ToBlob(base64), 'capture.jpg');
  return formData;
}

// ── API Functions ──

async function fetchDashboard(): Promise<SentinelDashboard> {
  const { data } = await apiClient.get('/sentinel/dashboard');

  // Backend returns nested structure — transform to flat KPIs
  const cameras = data.cameras || {};
  const tracking = data.tracking || {};
  const alerts = data.alerts || [];
  const zones = data.zones || [];

  return {
    cameras_active: cameras.online ?? 0,
    cameras_total: cameras.total ?? 0,
    active_tracking: tracking.active_tracks ?? 0,
    zones_monitored: zones.length,
    alerts_today: alerts.length,
    persons_enrolled: tracking.total_persons_today ?? 0,
    recent_events: alerts.slice(0, 10).map((a: Record<string, unknown>, i: number) => ({
      id: String(a.id || `alert-${i}`),
      type: String(a.type || 'zone_alert'),
      description: String(a.description || a.message || 'Alert'),
      timestamp: String(a.timestamp || new Date().toISOString()),
      camera_id: a.camera_id ? String(a.camera_id) : undefined,
      zone: a.zone ? String(a.zone) : undefined,
      confidence: typeof a.confidence === 'number' ? a.confidence : undefined,
    })),
  };
}

async function fetchStatus(): Promise<SentinelStatus> {
  const { data } = await apiClient.get('/sentinel/status');

  // Backend returns modules as { "reconnaissance_faciale": { status: "actif" }, ... }
  const mods = data.modules_biometriques || {};
  const isActive = (key: string) => {
    const mod = mods[key];
    if (!mod) return false;
    if (typeof mod === 'boolean') return mod;
    if (typeof mod === 'object' && mod.status) return mod.status === 'actif';
    return false;
  };

  // Map backend status to frontend enum
  const rawStatus = String(data.status || 'offline').toLowerCase();
  const status: SentinelStatus['status'] =
    rawStatus === 'active' || rawStatus === 'actif' ? 'operational' :
    rawStatus === 'degraded' ? 'degraded' : 'offline';

  return {
    status,
    uptime_seconds: data.uptime_seconds ?? 0,
    modules_biometriques: {
      face_detection: isActive('reconnaissance_faciale'),
      face_recognition: isActive('reconnaissance_faciale'),
      liveness_detection: isActive('anti_spoofing'),
      anti_spoofing: isActive('anti_spoofing'),
    },
    gpu_available: data.gpu_available ?? false,
    model_version: data.model_version ?? 'unknown',
    last_check: data.last_check ?? new Date().toISOString(),
  };
}

async function enrollFace(request: EnrollRequest): Promise<EnrollResponse> {
  // Backend expects: UploadFile image + query params name, clearance_level
  const formData = makeImageFormData(request.image_base64);
  const clearanceNum = CLEARANCE_MAP[request.clearance_level || 'employee'] || 2;

  const { data } = await apiClient.post('/sentinel/enroll', formData, {
    params: {
      name: request.person_name,
      clearance_level: clearanceNum,
    },
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return {
    success: data.status === 'enrolled',
    person_id: data.person_id || '',
    face_encoding_id: data.person_id || '',
    message: data.status === 'enrolled' ? `${request.person_name} enrolled successfully` : 'Enrollment failed',
  };
}

async function checkAccess(request: AccessCheckRequest): Promise<AccessCheckResponse> {
  // Backend expects: UploadFile image + query params zone_id, clearance_required (int)
  const formData = makeImageFormData(request.image_base64);
  const clearanceNum = CLEARANCE_MAP[request.required_clearance || 'employee'] || 2;

  const { data } = await apiClient.post('/sentinel/access-check', formData, {
    params: {
      zone_id: request.zone_id || 'default',
      clearance_required: clearanceNum,
    },
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return {
    access_granted: data.access_granted ?? false,
    person_id: null,
    person_name: data.person_name ?? null,
    confidence: data.confidence ?? 0,
    clearance_level: data.clearance_level != null ? String(data.clearance_level) : null,
    reason: data.reason ?? (data.access_granted ? 'Access granted' : 'Access denied'),
    liveness_passed: data.threat_level !== 'high',
  };
}

async function verifyIdentity(request: VerifyRequest): Promise<VerifyResponse> {
  // Backend expects: UploadFile image + query param name (NOT person_id)
  const formData = makeImageFormData(request.image_base64);

  const { data } = await apiClient.post('/sentinel/verify', formData, {
    params: {
      name: request.person_id, // Backend uses "name" as the identifier
    },
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return {
    verified: data.verified ?? false,
    confidence: data.similarity ?? 0,
    person_name: data.person_name ?? request.person_id,
    message: data.verified ? 'Identity verified' : 'Verification failed',
  };
}

async function identifyPerson(request: IdentifyRequest): Promise<IdentifyResponse> {
  // Backend expects: UploadFile image + query params threshold, top_k
  const formData = makeImageFormData(request.image_base64);

  const { data } = await apiClient.post('/sentinel/identify', formData, {
    params: {
      top_k: request.top_k ?? 5,
      threshold: 0.5,
    },
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  // Backend returns { identified, best_match, candidates, threshold }
  const candidates: Array<{ name: string; confidence: number; match: boolean }> =
    data.candidates || [];

  return {
    identified: data.identified ?? false,
    matches: candidates.map((c) => ({
      person_id: c.name,
      person_name: c.name,
      confidence: c.confidence,
      clearance_level: 'unknown',
    })),
    message: data.identified
      ? `Best match: ${data.best_match?.name ?? 'unknown'}`
      : 'No match found',
  };
}

async function checkLiveness(request: LivenessRequest): Promise<LivenessResponse> {
  // Backend expects: UploadFile image
  const formData = makeImageFormData(request.image_base64);

  const { data } = await apiClient.post('/sentinel/liveness-check', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return {
    is_live: data.is_live ?? false,
    confidence: data.liveness_score ?? 0,
    spoofing_type: data.is_live ? null : 'potential_spoof',
    message: data.is_live
      ? 'Live person detected'
      : 'Possible spoofing attack detected',
  };
}

// ── React Query Hooks ──

export function useSentinelDashboard() {
  return useQuery({
    queryKey: ['sentinel-dashboard'],
    queryFn: fetchDashboard,
    refetchInterval: 10_000,
    staleTime: 5_000,
  });
}

export function useSentinelStatus() {
  return useQuery({
    queryKey: ['sentinel-status'],
    queryFn: fetchStatus,
    refetchInterval: 15_000,
    staleTime: 10_000,
  });
}

export function useEnrollFace() {
  return useMutation({
    mutationFn: enrollFace,
  });
}

export function useAccessCheck() {
  return useMutation({
    mutationFn: checkAccess,
  });
}

export function useVerifyIdentity() {
  return useMutation({
    mutationFn: verifyIdentity,
  });
}

export function useIdentifyPerson() {
  return useMutation({
    mutationFn: identifyPerson,
  });
}

export function useCheckLiveness() {
  return useMutation({
    mutationFn: checkLiveness,
  });
}
